import { format } from "date-fns";

import type { Identifier } from "ra-core";

const CLOSED_STAGES = new Set(["lost"]);
const IDLE_STAGES = new Set(["lost", "delayed"]);

/** Date-only strings stay on the civil day; timestamps keep their clock time. */
export const toLocalDate = (value: string | Date): Date => {
  if (value instanceof Date) return value;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  return new Date(value);
};

export const dayKey = (value: string | Date): string =>
  format(toLocalDate(value), "yyyy-MM-dd");

/** 0–100 from the stage's place in the open pipeline. Lost deals stay at 0. */
export const stageProgress = (
  stage: string,
  stages: readonly { value: string }[],
): number => {
  if (CLOSED_STAGES.has(stage)) return 0;
  const active = stages.filter((item) => !IDLE_STAGES.has(item.value));
  const index = active.findIndex((item) => item.value === stage);
  if (index < 0 || active.length <= 1) return 0;
  return Math.round((index / (active.length - 1)) * 100);
};

/** A timestamp at local midnight is a date-only value and belongs in "all day". */
export const isAllDay = (value: string): boolean => {
  const date = toLocalDate(value);
  return (
    date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0
  );
};

/** Hours shown on the agenda. 8–20 always, plus any hour that actually has an item. */
export const timelineHours = (starts: readonly Date[], day: Date): number[] => {
  const hours = new Set<number>();
  for (let hour = 8; hour <= 20; hour += 1) hours.add(hour);
  const key = format(day, "yyyy-MM-dd");
  for (const start of starts) {
    if (format(start, "yyyy-MM-dd") !== key) continue;
    if (start.getHours() === 0 && start.getMinutes() === 0) continue;
    hours.add(start.getHours());
  }
  return [...hours].sort((left, right) => left - right);
};

export const agendaHref = (item: {
  source: string;
  contact_id?: Identifier | null;
  deal_id?: Identifier | null;
}): string | null => {
  switch (item.source) {
    case "task":
      // Internal (team) tasks have no contact: open the Tareas list.
      return item.contact_id != null
        ? `/contacts/${item.contact_id}/show?tab=tasks`
        : "/tasks";
    case "deal_closing":
    case "milestone":
      return item.deal_id != null ? `/informes/${item.deal_id}` : null;
    default:
      return null;
  }
};

export const plainText = (value: string, max = 140): string => {
  const plain = value
    .replace(/<[^>]+>/g, " ")
    .replace(/[#>*_`[\]]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (plain.length <= max) return plain;
  return `${plain.slice(0, max).trim()}…`;
};
